import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import LoginService from "../../../api/services/login_Service";
import { apiList } from "../../../api/apiList";
import { setLoginData, selectLoginData, setRememberMe } from './login.actions';
import { decrypt } from "../../../helpers/encrypt";

import TextInputFloat from "../../common/components/textInput/TextInputFloat";
import Logo from "../../common/components/Logo";
import Modal from "../../common/components/modal";

import "react-toastify/dist/ReactToastify.css";

const Login = ({ setIsLoading, showToastMessage }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { rememberMe, credential } = useSelector(selectLoginData);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (username && password) {
      try {
        setIsLoading(true);
        const data = await LoginService.login({
          Username: username,
          Password: password,
          ismobile: true,
          appVersion: apiList.appVersion,
        }).json();
        setIsLoading(false);

        if (data.message === "Your account is locked!") {
          openModal('777 Soccer', 'Your account is locked!');
          navigate("/login");
        } else {
          dispatch(setLoginData({ ...data, rememberMe, username, password }));
          navigate("/rules?loggedIn=true");
        }
      } catch (error) {
        setIsLoading(false);
        openModal('777 Soccer', 'Login Failed!');
      }
    }
  };

  const openModal = (title, content) => {
    setModalTitle(title);
    setModalContent(content);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const handleRememberMeChange = () => {
    dispatch(setRememberMe());
  };

  useEffect(() => {
    if (rememberMe && credential) {
      const decrypted = decrypt(credential);
      setUsername(decrypted?.username || '');
      setPassword(decrypted?.password || '');
    }
  }, [credential, rememberMe]);

  return (
    <div className="min-h-[70vh] w-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative">

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4">
        {/* Header Section */}
        <div className="text-center mb-8">
          
          <Logo />

          {/* Welcome Text */}
          <div className="space-y-2 mt-4">
            <h1 className="text-3xl font-bold text-[#228B22]">
              Welcome Back!
            </h1>
            <p className="text-sm text-gray-600 font-medium">
              Professional Sports Betting Platform
            </p>
          </div>
        </div>

        {/* Login Form */}
        <div className="w-full max-w-sm">
          <div className="bg-white/90 border border-gray-200 rounded-3xl p-6 shadow-md">
            <form onSubmit={handleLogin} className="space-y-5">
              {/* Username Field */}
              <div className="space-y-2">
                <label htmlFor="username" className="flex items-center text-sm font-bold text-[#228B22]">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  Username
                </label>
                <TextInputFloat
                  id="username"
                  placeholder="Enter your username"
                  value={username}
                  type="text"
                  setValue={setUsername}
                />
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <label htmlFor="password" className="flex items-center text-sm font-bold text-[#228B22]">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Password
                </label>
                <TextInputFloat
                  id="password"
                  placeholder="Enter your password"
                  value={password}
                  type="password"
                  setValue={setPassword}
                />
              </div>

              {/* Remember Me */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    className="w-5 h-5 text-[#228B22] bg-white border-2 border-[#228B22] rounded-lg"
                  />
                  <label htmlFor="remember-me" className="ml-3 text-sm font-semibold text-gray-700">
                    Remember Me
                  </label>
                </div>
                <div className="text-xs text-gray-500">
                  Encrypted
                </div>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-[#FFD700] via-yellow-400 to-[#FFD700] text-gray-900 font-bold text-lg p-4 rounded-2xl shadow-sm active:opacity-90"
              >
                Login
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-white/80 px-4 py-2 rounded-full border border-gray-200">
            <span className="text-sm text-gray-600 font-medium">Powered by Seven Tri</span>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <Modal title={modalTitle} content={modalContent} onClose={closeModal} />
      )}
    </div>
  );
};

export default Login;
